import { useRef, useState, useCallback } from 'react'

// AudioWorklet processor source — converts Float32 samples to Int16 and
// posts 100ms chunks (2400 samples at 24 kHz) back to the main thread.
const WORKLET_SOURCE = /* js */ `
class PCM16Processor extends AudioWorkletProcessor {
  constructor() {
    super()
    this._buffer = []
    this._chunkSize = 2400 // 100 ms at 24 kHz
  }

  process(inputs) {
    const input = inputs[0]
    if (!input || !input[0]) return true

    const samples = input[0]
    for (let i = 0; i < samples.length; i++) {
      // Clamp and convert Float32 → Int16
      const s = Math.max(-1, Math.min(1, samples[i]))
      this._buffer.push(s < 0 ? s * 0x8000 : s * 0x7fff)
    }

    while (this._buffer.length >= this._chunkSize) {
      const chunk = this._buffer.splice(0, this._chunkSize)
      const int16 = new Int16Array(chunk)
      this.port.postMessage({ int16 }, [int16.buffer])
    }

    return true
  }
}

registerProcessor('pcm16-processor', PCM16Processor)
`

export interface AudioCaptureHook {
  start: () => Promise<void>
  stop: () => void
  isCapturing: boolean
  error: string | null
}

/**
 * Captures microphone audio at 24 kHz and fires `onChunk` with Int16Array
 * chunks (~100 ms each). Uses an AudioWorklet for low-latency processing
 * so no external resampling library is needed.
 *
 * `getDeviceId` (optional) is read at `start()` to capture from a specific microphone — `''`/absent
 * uses the system default. To switch device mid-session, stop() then start() again.
 */
export function useAudioCapture(
  onChunk: (int16: Int16Array) => void,
  getDeviceId?: () => string | undefined,
): AudioCaptureHook {
  const [isCapturing, setIsCapturing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ctxRef = useRef<AudioContext | null>(null)
  const workletRef = useRef<AudioWorkletNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const onChunkRef = useRef(onChunk)
  onChunkRef.current = onChunk
  const getDeviceIdRef = useRef(getDeviceId)
  getDeviceIdRef.current = getDeviceId

  const start = useCallback(async () => {
    if (isCapturing) return
    setError(null)

    try {
      const deviceId = getDeviceIdRef.current?.()
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        },
      })
      streamRef.current = stream

      // Create AudioContext at 24 kHz — avoids manual resampling.
      // resume() is required on iOS Safari: AudioContext starts suspended
      // whenever the async chain (getUserMedia → addModule) outlasts the
      // user-gesture window, causing the worklet to produce silent chunks.
      const ctx = new AudioContext({ sampleRate: 24000 })
      await ctx.resume()
      ctxRef.current = ctx

      // Load worklet via Blob URL to avoid needing a separate file
      const blob = new Blob([WORKLET_SOURCE], { type: 'application/javascript' })
      const blobUrl = URL.createObjectURL(blob)
      await ctx.audioWorklet.addModule(blobUrl)
      URL.revokeObjectURL(blobUrl)

      const source = ctx.createMediaStreamSource(stream)
      const worklet = new AudioWorkletNode(ctx, 'pcm16-processor')
      workletRef.current = worklet

      worklet.port.onmessage = (ev: MessageEvent<{ int16: Int16Array }>) => {
        onChunkRef.current(ev.data.int16)
      }

      source.connect(worklet)
      worklet.connect(ctx.destination) // required for worklet to run in some browsers

      setIsCapturing(true)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      console.error('[AudioCapture] start error:', msg)
    }
  }, [isCapturing])

  const stop = useCallback(() => {
    workletRef.current?.disconnect()
    workletRef.current = null

    ctxRef.current?.close().catch(() => undefined)
    ctxRef.current = null

    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null

    setIsCapturing(false)
  }, [])

  return { start, stop, isCapturing, error }
}
